import { defineStore } from 'pinia';
import authService from '@/services/auth';
import profileService from '@/services/profile';
import ownerPortalApi from '@/services/ownerPortalApi';
import { saveStorageItem, loadStorageItem } from '@/services/storage';
import { GUEST, OWNER, MANAGEMENT_COMPANY_EMPLOYEE } from '@/constants/roles';
import * as subjects from '@/constants/subjects';

const APP_ROLE = 'app:role';

function getAccessRights(allRights = false) {
  return {
    read: !!allRights,
    write: !!allRights,
  };
}

const SUBJECTS_MAP = {
  announcements: subjects.ANNOUNCEMENTS,
  chat: subjects.CHAT,
  contractors: subjects.CONTRACTORS,
  invoices: subjects.INVOICES,
  projects: subjects.PROJECTS,
  clients: subjects.CLIENTS,
  employees: subjects.EMPLOYEES,
  dashboard: subjects.DASHBOARD,
  contracts: subjects.CONTRACTS,
  issues: subjects.ISSUES,
  issues_assign: subjects.ISSUES_ASIGN,
  issues_all: subjects.ISSUES_ALL,
  services: subjects.SERVICES,
  requests: subjects.REQUESTS,
  docuseal: subjects.DOCUSEAL,
  rooms: subjects.ROOMS,
  calendar: subjects.CALENDAR,
  events: subjects.EVENTS,
};

// Company feature flags — not CASL subjects
const COMPANY_FEATURE_SECTIONS = new Set(['odoo', 'owner_portal', 'esoft']);

function getSubjectForSectionType(sectionType) {
  const result = SUBJECTS_MAP[sectionType];

  if (!result && !COMPANY_FEATURE_SECTIONS.has(sectionType)) {
    // eslint-disable-next-line no-console
    console.warn('User access right unknown', sectionType);
  }

  return result;
}

export const useUserStore = defineStore('user', {
  state: () => ({
    role: 'guest',
    user: {},
  }),

  getters: {
    rights(state) {
      const rights = {};
      const sections = state.user?.sections;
      const companySections = state.user?.companySections;

      if (!sections) return [];

      sections.forEach(section => {
        const subject = getSubjectForSectionType(section.name);
        if (!subject) return;

        if (!rights[subject]) {
          rights[subject] = getAccessRights();
        }

        rights[subject][section.type] = true;
      });

      companySections.forEach(section => {
        const subject = getSubjectForSectionType(section.name);

        if (!subject) return;

        if (subject === 'docuseal' && !rights[subject]) {
          rights[subject] = getAccessRights(true);
        }
      });

      return rights;
    },

    companySectionsRights(state) {
      return state.user?.companySections;
    },

    uid(state) {
      return state.user?.id;
    },
  },

  actions: {
    setUserRole(role) {
      this.role = role;
      saveStorageItem(APP_ROLE, role);
    },

    initUserRole() {
      const role = loadStorageItem(APP_ROLE) || 'guest';
      this.role = role;
    },

    logout() {
      this.role = GUEST;
      this.user = {};
      authService.logout();
    },

    setUser(user) {
      this.user = user;
    },

    fetchUser() {
      const fetchApi = this.role === 'owner' ? ownerPortalApi : profileService;
      return fetchApi
        .get()
        .then(user => {
          this.setUser(user);
        })
        .catch(error => {
          if (error.response.status === 403) {
            if ([OWNER, MANAGEMENT_COMPANY_EMPLOYEE].includes(error.response.data.you_role)) {
              saveStorageItem(APP_ROLE, error.response.data.you_role);
              this.role = error.response.data.you_role;
              window.location.reload();
            }
          }
        });
    },
  },
});
